import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { useApiClient } from '../contexts/ApiContext';
import { queryKeys } from '../lib/queryClient';
import type { CreateCaseBattleRequest } from '../services/generated/Api';
import { useAuthedInfiniteQuery } from './useAuthedQuery';

export function useActiveCaseBattles(enabled = true) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.caseBattles,
    queryFn: () => apiClient.api.caseBattleControllerGetActiveCaseBattles(),
    // 30s staleTime aligns with the SSR revalidate window so the
    // hydrated cache survives mount; refetchInterval still keeps the
    // list fresh in the background.
    staleTime: 30 * 1000,
    refetchInterval: 30000,
    enabled,
  });
}

export function useCaseBattleDetails(battleId: string, enabled = true) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.caseBattleDetails(battleId),
    queryFn: () => apiClient.api.caseBattleControllerGetCaseBattleDetails(battleId),
    enabled: enabled && !!battleId,
    // 30s aligns with the SSR revalidate window in
    // (public)/case-battles/[battleId]/page.tsx — without it
    // refetchOnMount: 'always' would discard the hydrated cache and
    // double-fetch on every mount. WebSocket invalidation still
    // forces a refetch on real updates via queryClient.invalidate*.
    staleTime: 30 * 1000,
  });
}

const HISTORY_PAGE_SIZE = 20;

export function useCaseBattleHistoryAllInfinite(enabled = true) {
  const apiClient = useApiClient();

  return useInfiniteQuery({
    queryKey: queryKeys.caseBattleHistoryAll,
    queryFn: async ({ pageParam = 0 }) => {
      const response = await apiClient.api.caseBattleControllerGetAllCaseBattleHistory({
        skip: pageParam,
        take: HISTORY_PAGE_SIZE,
      });
      return {
        items: response.items,
        total: response.total,
        skip: pageParam,
        hasMore: pageParam + HISTORY_PAGE_SIZE < response.total,
      };
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage.hasMore) return undefined;
      return lastPage.skip + HISTORY_PAGE_SIZE;
    },
    initialPageParam: 0,
    enabled,
  });
}

export function useCaseBattleHistoryMineInfinite(enabled = true) {
  const apiClient = useApiClient();

  return useAuthedInfiniteQuery({
    queryKey: queryKeys.caseBattleHistoryMine,
    queryFn: async ({ pageParam = 0 }) => {
      const response = await apiClient.api.caseBattleControllerGetUserCaseBattleHistory({
        skip: pageParam,
        take: HISTORY_PAGE_SIZE,
      });
      return {
        items: response.items,
        total: response.total,
        skip: pageParam,
        hasMore: pageParam + HISTORY_PAGE_SIZE < response.total,
      };
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage.hasMore) return undefined;
      return lastPage.skip + HISTORY_PAGE_SIZE;
    },
    initialPageParam: 0,
    enabled,
  });
}

export function useCreateCaseBattle() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCaseBattleRequest) =>
      apiClient.api.caseBattleControllerCreateCaseBattle(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.caseBattles });
      queryClient.invalidateQueries({ queryKey: queryKeys.caseBattlesCount });
      queryClient.invalidateQueries({ queryKey: queryKeys.userActiveBattlesCount });
      queryClient.invalidateQueries({ queryKey: queryKeys.profile });
    },
  });
}

export function useJoinCaseBattle() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (battleId: string) =>
      apiClient.api.caseBattleControllerJoinCaseBattle(battleId),
    onSuccess: (_data, battleId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.caseBattles });
      queryClient.invalidateQueries({ queryKey: queryKeys.caseBattlesCount });
      queryClient.invalidateQueries({ queryKey: queryKeys.userActiveBattlesCount });
      queryClient.invalidateQueries({ queryKey: queryKeys.caseBattleDetails(battleId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.profile });
    },
  });
}

export function useLeaveCaseBattle() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (battleId: string) =>
      apiClient.api.caseBattleControllerLeaveCaseBattle(battleId),
    onSuccess: (_data, battleId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.caseBattles });
      queryClient.invalidateQueries({ queryKey: queryKeys.caseBattlesCount });
      queryClient.invalidateQueries({ queryKey: queryKeys.userActiveBattlesCount });
      queryClient.invalidateQueries({ queryKey: queryKeys.caseBattleDetails(battleId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.profile });
    },
  });
}

export function useActiveCaseBattlesCount() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: queryKeys.caseBattlesCount,
    queryFn: async () => {
      // Try to get count from existing cache first
      const cachedBattles = queryClient.getQueryData(queryKeys.caseBattles);
      if (Array.isArray(cachedBattles)) {
        return cachedBattles.length;
      }
      // Otherwise fetch the data
      const battles = await apiClient.api.caseBattleControllerGetActiveCaseBattles();
      return battles?.length || 0;
    },
    staleTime: 60 * 1000, // 1 minute
  });
}

export function useUserActiveBattlesCount() {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.userActiveBattlesCount,
    queryFn: () => apiClient.api.caseBattleControllerGetUserActiveBattlesCount(),
    staleTime: 30 * 1000, // 30 seconds
  });
}
